import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  Destroyable,
  Rental,
  Toaster,
  untilDestroy,
  updateMultipleRentalsComplete,
} from '@tokeet-frontend/tv3-platform'
import { Store } from '@ngrx/store'
import * as R from 'ramda'
import { BehaviorSubject } from 'rxjs'
import {
  ComplianceService,
  EvisitorConnection,
  EvisitorFacility,
  encodeFacilityCode,
} from '@tv3/containers/compliance/compliance.service'
import { AmplitudeService } from '@tv3/services/amplitude.service'

@Component({
  selector: 'app-rental-compliance',
  templateUrl: './rental-compliance.component.html',
  styleUrls: ['./rental-compliance.component.scss'],
})
export class RentalComplianceComponent extends Destroyable implements OnInit, OnChanges {
  @Input() rental: Rental

  showEvisitor = false
  connections: EvisitorConnection[] = []
  facilities: EvisitorFacility[] = []
  selectedConnectionPkey: string | null = null
  matchedFacility: EvisitorFacility | null = null

  isLoadingConnections$ = new BehaviorSubject(false)
  isLoadingFacilities$ = new BehaviorSubject(false)
  isLinking$ = new BehaviorSubject(false)
  facilitySearchCtrl = new FormControl('')

  constructor(
    private complianceService: ComplianceService,
    private amplitudeService: AmplitudeService,
    private toaster: Toaster,
    private store: Store
  ) {
    super()
  }

  ngOnInit(): void {
    this.checkEvisitorEligibility()
    if (this.showEvisitor) {
      this.loadConnections()
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.rental && !changes.rental.firstChange) {
      this.rental = R.pathOr(this.rental, ['rental', 'currentValue'], changes)
      this.checkEvisitorEligibility()
      if (this.showEvisitor) {
        if (this.connections.length) {
          this.syncSelectionWithRental()
        } else {
          this.loadConnections()
        }
      }
    }
  }

  private checkEvisitorEligibility() {
    if (!this.rental) {
      this.showEvisitor = false
      return
    }

    // Show eVisitor for all rentals regardless of country
    this.showEvisitor = true
  }

  private loadConnections() {
    this.isLoadingConnections$.next(true)
    this.complianceService
      .getEvisitorConnections()
      .pipe(untilDestroy(this))
      .subscribe({
        next: (connections) => {
          this.connections = connections || []
          this.isLoadingConnections$.next(false)
          this.syncSelectionWithRental()
        },
        error: (err) => {
          console.error('Failed to load eVisitor connections:', err)
          this.connections = []
          this.isLoadingConnections$.next(false)
        },
      })
  }

  private syncSelectionWithRental() {
    const rentalGatewayId = this.rental?.evisitor?.evisitor_gateway_id
    if (rentalGatewayId) {
      const match = this.connections.find((conn) => conn.pkey === rentalGatewayId)
      this.selectedConnectionPkey = match ? match.pkey : null
    } else {
      // Auto-select if there's only one connection available
      if (this.connections.length === 1) {
        this.selectedConnectionPkey = this.connections[0].pkey
      } else {
        this.selectedConnectionPkey = null
      }
    }

    if (this.selectedConnectionPkey) {
      this.loadFacilities(this.selectedConnectionPkey)
    } else {
      this.facilities = []
      this.matchedFacility = null
    }
  }

  onConnectionSelected(connectionPkey: string) {
    this.selectedConnectionPkey = connectionPkey
    this.loadFacilities(connectionPkey)
    this.amplitudeService.logEvent('compliance_connection_selected')
  }

  private loadFacilities(connectionPkey: string) {
    if (!connectionPkey) {
      this.facilities = []
      this.matchedFacility = null
      return
    }

    this.isLoadingFacilities$.next(true)
    this.complianceService
      .getEvisitorFacilities(connectionPkey)
      .pipe(untilDestroy(this))
      .subscribe({
        next: (facilities) => {
          this.facilities = facilities || []
          this.isLoadingFacilities$.next(false)
          this.updateMatchedFacility()
        },
        error: (err) => {
          console.error('Failed to load eVisitor facilities:', err)
          this.facilities = []
          this.matchedFacility = null
          this.isLoadingFacilities$.next(false)
        },
      })
  }

  private updateMatchedFacility() {
    const facilityId = this.rental?.evisitor?.evisitor_facility_id
    if (!facilityId || !this.facilities.length) {
      this.matchedFacility = null
      return
    }

    this.matchedFacility = this.facilities.find((facility) => facility.id === facilityId) || null
  }

  onLinkFacility(facility: EvisitorFacility) {
    if (!this.rental || !this.selectedConnectionPkey) {
      return
    }

    this.isLinking$.next(true)
    const encodedCode = encodeFacilityCode(facility.code)
    this.complianceService
      .updateRentalEvisitorConfig(this.rental.id, {
        evisitor_facility_id: facility.id,
        evisitor_facility_code: encodedCode,
        evisitor_gateway_id: this.selectedConnectionPkey,
      })
      .pipe(untilDestroy(this))
      .subscribe({
        next: () => {
          const evisitorData = {
            evisitor_facility_id: facility.id,
            evisitor_facility_code: encodedCode,
            evisitor_gateway_id: this.selectedConnectionPkey || '',
          }

          // Update NgRx store so other components (like EvisitorMappingComponent) see the change
          this.store.dispatch(
            updateMultipleRentalsComplete({
              updates: [
                {
                  id: this.rental.id,
                  changes: { evisitor: evisitorData },
                },
              ],
            })
          )

          this.matchedFacility = facility
          this.rental.evisitor = evisitorData
          this.isLinking$.next(false)
          this.toaster.success($localize`Facility linked.`)
          this.amplitudeService.logEvent('compliance_facility_linked')
        },
        error: (err) => {
          console.error('Failed to link eVisitor facility:', err)
          this.isLinking$.next(false)
          this.amplitudeService.logEvent('compliance_facility_link_failed')
        },
      })
  }

  onUnlinkFacility() {
    if (!this.rental) {
      return
    }

    this.isLinking$.next(true)
    this.complianceService
      .updateRentalEvisitorConfig(this.rental.id, {})
      .pipe(untilDestroy(this))
      .subscribe({
        next: () => {
          // Update NgRx store so other components (like EvisitorMappingComponent) see the change
          this.store.dispatch(
            updateMultipleRentalsComplete({
              updates: [
                {
                  id: this.rental.id,
                  changes: { evisitor: undefined },
                },
              ],
            })
          )

          this.matchedFacility = null
          this.rental.evisitor = undefined
          this.isLinking$.next(false)
          this.toaster.success($localize`Facility unlinked.`)
          this.amplitudeService.logEvent('compliance_facility_unlinked')
        },
        error: (err) => {
          console.error('Failed to unlink eVisitor facility:', err)
          this.isLinking$.next(false)
          this.amplitudeService.logEvent('compliance_facility_unlink_failed')
        },
      })
  }

  get selectedConnection(): EvisitorConnection | undefined {
    return this.connections.find((conn) => conn.pkey === this.selectedConnectionPkey)
  }

  get filteredFacilities(): EvisitorFacility[] {
    const term = (this.facilitySearchCtrl.value || '').toString().trim().toLowerCase()

    return this.facilities.filter((facility) => {
      return (
        !term ||
        facility.name.toLowerCase().includes(term) ||
        facility.id.toLowerCase().includes(term) ||
        (facility.address || '').toLowerCase().includes(term) ||
        (facility.city || '').toLowerCase().includes(term)
      )
    })
  }
}
